<template>
  <div class="subtitle-track">
    <!-- Track Label -->
    <div class="track-label">
      <svg width="20" height="15" viewBox="0 0 20 15" fill="none">
        <path
          d="M2 3a1 1 0 011-1h14a1 1 0 011 1v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3zm1 0v9h14V3H3z"
          fill="#646464"
        />
        <path d="M5 6h10v1H5V6zm0 2h8v1H5V8zm0 2h6v1H5v-1z" fill="#646464" />
      </svg>
    </div>

    <!-- Subtitle Blocks Container -->
    <div class="subtitle-blocks-container" ref="subtitleContainerRef">
      <div
        v-for="subtitle in visibleSubtitles"
        :key="subtitle.id"
        :data-subtitle-id="subtitle.id"
        class="subtitle-block"
        :class="{
          'subtitle-block--active': isSubtitleActive(subtitle),
          'subtitle-block--current': isCurrentSubtitle(subtitle),
        }"
        :style="getSubtitleBlockStyle(subtitle)"
        @click.stop="handleSubtitleClick(subtitle)"
      >
        <div class="subtitle-text">
          {{ subtitle.text }}
        </div>
        <div class="subtitle-time">
          {{ formatTime(subtitle.startTime) }} - {{ formatTime(subtitle.endTime) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import type { SRTSubtitle } from '@/services/srt-parser'

interface Props {
  subtitles: SRTSubtitle[]
  duration: number
  currentTime: number
  startTime: number
  endTime: number
  trackWidth: number
  pixelsPerSecond: number
  isPlaying?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isPlaying: false,
})

interface Emits {
  'subtitle-click': [subtitle: SRTSubtitle]
  'time-click': [time: number]
}

const emit = defineEmits<Emits>()

// Refs
const subtitleContainerRef = ref<HTMLElement | null>(null)

// Computed
const visibleSubtitles = computed(() => {
  return props.subtitles.filter(
    (subtitle) => subtitle.startTime < props.endTime && subtitle.endTime > props.startTime,
  )
})

const currentSubtitle = computed(() => {
  return props.subtitles.find(
    (subtitle) => props.currentTime >= subtitle.startTime && props.currentTime <= subtitle.endTime,
  )
})

// Methods
const getSubtitleBlockStyle = (subtitle: SRTSubtitle) => {
  const startX = (subtitle.startTime - props.startTime) * props.pixelsPerSecond
  const endX = (subtitle.endTime - props.startTime) * props.pixelsPerSecond
  const width = Math.max(20, endX - startX) // Minimum width of 20px

  return {
    left: `${startX}px`,
    width: `${width}px`,
  }
}

const isSubtitleActive = (subtitle: SRTSubtitle): boolean => {
  return props.currentTime >= subtitle.startTime && props.currentTime <= subtitle.endTime
}

const isCurrentSubtitle = (subtitle: SRTSubtitle): boolean => {
  return currentSubtitle.value?.id === subtitle.id
}

const handleSubtitleClick = (subtitle: SRTSubtitle) => {
  emit('subtitle-click', subtitle)
  // Note: time-click is emitted separately for time-specific interactions
  // subtitle-click now handles playhead movement to subtitle start time
}

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.round((seconds % 1) * 1000)

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
}

// Auto-scroll to current subtitle
const scrollToCurrentSubtitle = () => {
  if (!subtitleContainerRef.value || !currentSubtitle.value) return

  const container = subtitleContainerRef.value
  const currentElement = container.querySelector(
    `[data-subtitle-id="${currentSubtitle.value.id}"]`,
  ) as HTMLElement

  if (currentElement) {
    const containerRect = container.getBoundingClientRect()
    const elementRect = currentElement.getBoundingClientRect()

    // Check if element is outside visible area
    if (elementRect.left < containerRect.left || elementRect.right > containerRect.right) {
      currentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  }
}

// Watch for current time changes to auto-scroll
watch(
  () => props.currentTime,
  () => {
    if (props.isPlaying) {
      scrollToCurrentSubtitle()
    }
  },
)

// Watch for current subtitle changes
watch(currentSubtitle, () => {
  if (props.isPlaying) {
    scrollToCurrentSubtitle()
  }
})
</script>

<style scoped>
.subtitle-track {
  display: flex;
  height: 60px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
  position: relative;
  z-index: 20; /* Higher than timeline click overlay (z-index: 10) */
}

.track-label {
  width: 40px;
  background: #f3f4f6;
  border-right: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.subtitle-blocks-container {
  flex: 1;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  background: #fafafa;
}

.subtitle-block {
  position: absolute;
  top: 8px;
  height: 44px;
  background: #8b5cf6;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.subtitle-block:hover {
  background: #7c3aed;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.subtitle-block--active {
  background: #6d28d9;
  box-shadow: 0 0 0 2px #a78bfa;
}

.subtitle-block--current {
  background: #5b21b6;
  box-shadow: 0 0 0 2px #c4b5fd;
  transform: translateY(-2px);
}

.subtitle-text {
  color: white;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  display: flex;
  align-items: center;
}

.subtitle-time {
  color: rgba(255, 255, 255, 0.8);
  font-size: 9px;
  font-weight: 400;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Responsive text sizing */
.subtitle-block[style*='width: 20px'] .subtitle-text,
.subtitle-block[style*='width: 30px'] .subtitle-text,
.subtitle-block[style*='width: 40px'] .subtitle-text {
  font-size: 10px;
}

.subtitle-block[style*='width: 20px'] .subtitle-time,
.subtitle-block[style*='width: 30px'] .subtitle-time,
.subtitle-block[style*='width: 40px'] .subtitle-time {
  display: none;
}

/* Scrollbar styling */
.subtitle-blocks-container::-webkit-scrollbar {
  height: 4px;
}

.subtitle-blocks-container::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.subtitle-blocks-container::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.subtitle-blocks-container::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
